import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useWallet } from "@/context/WalletContext";
import { createApiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import { useLiveBotEvents } from "@/hooks/useLiveBotEvents";
import {
  Zap,
  ArrowLeft,
  Plus,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Flame,
} from "lucide-react";

// Polls every 30s on RUNNING bots. Pauses when tab is hidden (visibility API)
// so we don't waste cellular data while the user isn't looking.
const POLL_INTERVAL_MS = 30_000;
const BOT_TICK_INTERVAL_S = 60; // matches backend BOT_TICK_INTERVAL_SECONDS
const RECENT_TRADE_WINDOW_S = 180; // bot is "hot" if it traded in last 3 minutes

function timeAgo(isoStr) {
  if (!isoStr) return null;
  try {
    const then = new Date(isoStr).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.floor((now - then) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return null;
  }
}

// Returns seconds remaining until next tick (0-60) based on last_ticked_at.
// Returns null if we don't know when the bot last ticked.
function nextTickIn(lastTickedIso) {
  if (!lastTickedIso) return null;
  try {
    const last = new Date(lastTickedIso).getTime();
    const elapsed = (Date.now() - last) / 1000;
    const remaining = Math.max(0, BOT_TICK_INTERVAL_S - (elapsed % BOT_TICK_INTERVAL_S));
    return Math.ceil(remaining);
  } catch {
    return null;
  }
}

// 0..1 progress through the current tick cycle.
function tickProgress(lastTickedIso) {
  if (!lastTickedIso) return 0;
  const r = nextTickIn(lastTickedIso);
  if (r === null) return 0;
  return Math.max(0, Math.min(1, 1 - r / BOT_TICK_INTERVAL_S));
}

function secondsSince(isoStr) {
  if (!isoStr) return Infinity;
  try {
    return (Date.now() - new Date(isoStr).getTime()) / 1000;
  } catch {
    return Infinity;
  }
}

export default function BotsList() {
  const navigate = useNavigate();
  const { isConnected, getAuthHeaders } = useWallet();
  const authenticatedApi = createApiService(getAuthHeaders);

  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  // re-render trigger so per-second labels (countdown, progress bar, time-ago) refresh
  // eslint-disable-next-line no-unused-vars
  const [nowTick, setNowTick] = useState(0);

  // Track previous P&L and trade count per bot so we can animate diffs
  const prevSnapshotRef = useRef({}); // {bot_id: {pnl_pct, total_trades}}
  const [flashes, setFlashes] = useState({}); // {bot_id: "up" | "down" | null}
  const [tradeBumps, setTradeBumps] = useState({}); // {bot_id: +N delta last 30s}
  // Rolling live-trade ticker: [{bot_id, name, pnl_pct, delta, ts}]
  const [tradeTicker, setTradeTicker] = useState([]);

  const fetchBots = useCallback(async () => {
    try {
      let res;
      if (isConnected) {
        res = await authenticatedApi.getBots();
      } else {
        res = await authenticatedApi.get("/prediction-bots/public");
      }
      if (res?.data?.success) {
        const next = res.data.bots || [];

        // Diff against previous snapshot to detect changes worth animating
        const newFlashes = {};
        const newBumps = {};
        const newTickerEntries = [];
        const nowIso = new Date().toISOString();
        for (const b of next) {
          const id = b.id;
          const prev = prevSnapshotRef.current[id];
          if (prev) {
            if ((b.total_pnl_pct || 0) > (prev.pnl_pct || 0)) newFlashes[id] = "up";
            else if ((b.total_pnl_pct || 0) < (prev.pnl_pct || 0)) newFlashes[id] = "down";
            const delta = (b.total_trades || 0) - (prev.total_trades || 0);
            if (delta > 0) {
              newBumps[id] = delta;
              newTickerEntries.push({
                bot_id: id,
                name: b.name || id.slice(-6),
                pnl_pct: b.total_pnl_pct || 0,
                delta,
                ts: nowIso,
              });
            }
          }
          prevSnapshotRef.current[id] = {
            pnl_pct: b.total_pnl_pct || 0,
            total_trades: b.total_trades || 0,
          };
        }
        setBots(next);
        if (Object.keys(newFlashes).length > 0) {
          setFlashes((curr) => ({ ...curr, ...newFlashes }));
          setTimeout(() => setFlashes({}), 1800);
        }
        if (Object.keys(newBumps).length > 0) {
          setTradeBumps((curr) => ({ ...curr, ...newBumps }));
          setTimeout(() => setTradeBumps({}), 4000);
        }
        if (newTickerEntries.length > 0) {
          setTradeTicker((curr) => [...newTickerEntries, ...curr].slice(0, 12));
        }
        setLastRefreshAt(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch bots:", err);
      if (loading) toast.error("Failed to load bots");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Initial load + reload on connection change
  useEffect(() => {
    fetchBots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Background polling — every 30s, pause when tab is hidden
  useEffect(() => {
    let interval;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === "visible") fetchBots();
      }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) clearInterval(interval);
      interval = null;
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchBots();
        start();
      } else {
        stop();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1-second ticker so time-ago labels + next-tick countdown re-render live
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Live WebSocket — pushes trade open/close events within ~1s of execution.
  // Falls back gracefully to the 30s polling above if the WS can't connect.
  const {
    connected: wsConnected,
    flashes: wsFlashes,
    tradeBumps: wsBumps,
  } = useLiveBotEvents({
    onEvent: (ev) => {
      // Push into the TAPE ticker
      setTradeTicker((curr) =>
        [
          {
            bot_id: ev.bot_id,
            name: ev.name || (ev.bot_id || "").slice(-6),
            pnl_pct: ev.pnl_pct || 0,
            delta: 1,
            ts: ev.ts || new Date().toISOString(),
            kind: ev.type,
            reason: ev.reason,
          },
          ...curr,
        ].slice(0, 20)
      );
      // Optimistically update this bot's row
      setBots((curr) =>
        curr.map((b) =>
          b.id === ev.bot_id
            ? {
                ...b,
                total_pnl_pct:
                  typeof ev.pnl_pct === "number" ? ev.pnl_pct : b.total_pnl_pct,
                total_trades: (b.total_trades || 0) + 1,
                last_trade_at: ev.ts || new Date().toISOString(),
              }
            : b
        )
      );
    },
  });
  // Merge WS-driven animation state with poll-driven state
  const mergedFlashes = { ...flashes, ...wsFlashes };
  const mergedBumps = { ...tradeBumps, ...wsBumps };

  const sortedBots = useMemo(
    () =>
      bots
        .slice()
        .sort((a, b) => (b.total_pnl_pct || 0) - (a.total_pnl_pct || 0)),
    [bots]
  );
  const runningCount = bots.filter((b) => b.status === "running").length;
  const aggregateTrades = bots.reduce((acc, b) => acc + (b.total_trades || 0), 0);
  // Average P&L across RUNNING bots only — killed bots include legacy
  // historical-data corruption (int64 overflows) that would skew the banner.
  const runningBots = bots.filter((b) => b.status === "running");
  const aggregatePnlPct =
    runningBots.length > 0
      ? runningBots.reduce((acc, b) => acc + (b.total_pnl_pct || 0), 0) /
        runningBots.length
      : 0;
  const tradesThisPoll = Object.values(tradeBumps).reduce((a, b) => a + b, 0);
  const hotCount = bots.filter(
    (b) => b.status === "running" && secondsSince(b.last_trade_at) < RECENT_TRADE_WINDOW_S
  ).length;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="px-2"
                data-testid="botslist-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-secondary" />
                  Prediction Bots
                </h1>
                <p className="text-xs text-muted-foreground">
                  {bots.length} bot{bots.length !== 1 ? "s" : ""} ·{" "}
                  <span className="text-green-400">{runningCount} running</span>
                  {hotCount > 0 && (
                    <>
                      {" · "}
                      <span className="text-orange-400 inline-flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        {hotCount} hot
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchBots}
                disabled={loading}
                className="px-3"
                data-testid="botslist-refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/bot/create")}
                data-testid="botslist-deploy-new"
              >
                <Plus className="w-4 h-4 mr-1" /> Deploy
              </Button>
            </div>
          </div>

          {/* Live Activity Pulse banner */}
          <div
            className="mb-3 rounded-lg border border-green-500/20 bg-green-500/5 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
            data-testid="botslist-pulse-banner"
          >
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${wsConnected ? "bg-green-400" : "bg-yellow-400"} opacity-75`} />
                <span className={`relative inline-flex h-2 w-2 rounded-full ${wsConnected ? "bg-green-400" : "bg-yellow-400"}`} />
              </span>
              <span
                className={`font-mono ${wsConnected ? "text-green-300" : "text-yellow-300"}`}
                data-testid="botslist-live-status"
                title={wsConnected ? "Real-time WebSocket connected" : "Falling back to 30s polling"}
              >
                {wsConnected ? "LIVE" : "POLL"}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Activity className="w-3 h-3" />
              <span>{aggregateTrades.toLocaleString()} total trades</span>
            </div>
            <div
              className={`flex items-center gap-1.5 font-mono ${
                aggregatePnlPct >= 0 ? "text-green-400" : "text-red-400"
              }`}
              data-testid="botslist-aggregate-pnl"
            >
              {aggregatePnlPct >= 0 ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              avg {aggregatePnlPct >= 0 ? "+" : ""}
              {aggregatePnlPct.toFixed(2)}%
            </div>
            {tradesThisPoll > 0 && (
              <div
                className="flex items-center gap-1.5 text-green-400 font-semibold animate-pulse"
                data-testid="botslist-trades-this-poll"
              >
                <TrendingUp className="w-3 h-3" /> +{tradesThisPoll} new
              </div>
            )}
            <div className="flex items-center gap-1.5 text-muted-foreground ml-auto">
              <Clock className="w-3 h-3" />
              <span>
                {lastRefreshAt
                  ? `Updated ${timeAgo(lastRefreshAt.toISOString())}`
                  : "Loading…"}
              </span>
            </div>
          </div>

          {/* Live trade ticker - shows recent trade events in a scrolling row */}
          {tradeTicker.length > 0 && (
            <div
              className="mb-3 rounded-lg border border-secondary/20 bg-secondary/5 px-3 py-2 overflow-hidden"
              data-testid="botslist-trade-ticker"
            >
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-secondary shrink-0 flex items-center gap-1">
                  <Flame className="w-3 h-3" /> TAPE
                </span>
                <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap scrollbar-none">
                  {tradeTicker.map((ev, i) => (
                    <span
                      key={`${ev.bot_id}-${ev.ts}-${i}`}
                      className="inline-flex items-center gap-1.5 shrink-0 animate-slide-in"
                    >
                      <span className="text-muted-foreground">{timeAgo(ev.ts)}</span>
                      <span className="font-semibold">{ev.name}</span>
                      {ev.kind === "close" ? (
                        <span className="text-orange-300 uppercase text-[10px] tracking-wider">
                          close{ev.reason ? ` · ${ev.reason}` : ""}
                        </span>
                      ) : ev.kind === "open" ? (
                        <span className="text-cyan-300 uppercase text-[10px] tracking-wider">
                          open
                        </span>
                      ) : (
                        <span className="text-green-400">+{ev.delta} trade{ev.delta !== 1 ? "s" : ""}</span>
                      )}
                      <span
                        className={
                          ev.pnl_pct >= 0 ? "text-green-400" : "text-red-400"
                        }
                      >
                        {ev.pnl_pct >= 0 ? "+" : ""}
                        {ev.pnl_pct.toFixed(2)}%
                      </span>
                      <span className="text-muted-foreground">·</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {loading && bots.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                Loading bots…
              </CardContent>
            </Card>
          ) : bots.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No bots deployed yet
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-muted-foreground">
                  Sorted by P&L (highest first)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sortedBots.map((bot) => {
                    const flash = mergedFlashes[bot.id];
                    const bump = mergedBumps[bot.id];
                    const lastTradeAgo = timeAgo(bot.last_trade_at);
                    const lastTickedIso =
                      bot.tick_state?.last_ticked_at || bot.last_trade_at;
                    const tickIn =
                      bot.status === "running" ? nextTickIn(lastTickedIso) : null;
                    const progress =
                      bot.status === "running" ? tickProgress(lastTickedIso) : 0;
                    const isHot =
                      bot.status === "running" &&
                      secondsSince(bot.last_trade_at) < RECENT_TRADE_WINDOW_S;
                    return (
                      <div
                        key={bot.id}
                        onClick={() => navigate(`/bot/${bot.id}`)}
                        className={`relative border rounded-lg p-4 hover:border-secondary/40 cursor-pointer transition-all overflow-hidden ${
                          flash === "up"
                            ? "ring-2 ring-green-400/40"
                            : flash === "down"
                            ? "ring-2 ring-red-400/40"
                            : ""
                        } ${isHot ? "border-orange-500/40 shadow-[0_0_18px_-6px_rgba(251,146,60,0.45)]" : ""}`}
                        data-testid={`bot-row-${bot.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`p-3 rounded-lg ${
                                bot.status === "running"
                                  ? "bg-green-500/10"
                                  : "bg-secondary/10"
                              }`}
                            >
                              <Zap
                                className={`w-6 h-6 ${
                                  bot.status === "running"
                                    ? "text-green-400"
                                    : "text-secondary"
                                }`}
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold truncate">
                                  {bot.name
                                    ? `Bot ${bot.name}`
                                    : `Bot ${bot.id?.slice(-6) || "Unknown"}`}
                                </h3>
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${
                                    bot.status === "running"
                                      ? "bg-green-500/20 text-green-400 border-green-500/30"
                                      : bot.status === "killed"
                                      ? "bg-red-500/20 text-red-400 border-red-500/30"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {bot.status === "running" && (
                                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />
                                  )}
                                  {bot.status?.toUpperCase() || "STOPPED"}
                                </Badge>
                                {isHot && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] bg-orange-500/15 text-orange-300 border-orange-500/30 inline-flex items-center gap-1"
                                    data-testid={`bot-hot-${bot.id}`}
                                  >
                                    <Flame className="w-2.5 h-2.5" /> HOT
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Balance:{" "}
                                {(
                                  (bot.total_balance_stxoshi || 0) / 1_000_000
                                ).toLocaleString()}{" "}
                                STXOSHI
                              </p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                                {lastTradeAgo && (
                                  <span data-testid={`bot-last-trade-${bot.id}`}>
                                    Last trade {lastTradeAgo}
                                  </span>
                                )}
                                {tickIn !== null && (
                                  <span
                                    className="text-green-400/80 font-mono"
                                    data-testid={`bot-next-tick-${bot.id}`}
                                  >
                                    Next tick in {tickIn}s
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 shrink-0">
                            <div className="text-right">
                              <p
                                className={`font-mono font-semibold flex items-center gap-1 justify-end transition-colors ${
                                  (bot.total_pnl_pct || 0) >= 0
                                    ? "text-green-400"
                                    : "text-red-400"
                                } ${flash ? "animate-pulse" : ""}`}
                                data-testid={`bot-pnl-${bot.id}`}
                              >
                                {(bot.total_pnl_pct || 0) >= 0 ? (
                                  <TrendingUp className="w-4 h-4" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                                {(bot.total_pnl_pct || 0) >= 0 ? "+" : ""}
                                {(bot.total_pnl_pct || 0).toFixed(2)}%
                              </p>
                              <p
                                className="text-xs text-muted-foreground"
                                data-testid={`bot-trades-${bot.id}`}
                              >
                                {(bot.total_trades || 0).toLocaleString()} trades
                                {bump && (
                                  <span className="ml-1 text-green-400 font-semibold animate-pulse">
                                    +{bump}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Tick progress bar — fills as next tick approaches */}
                        {bot.status === "running" && (
                          <div
                            className="absolute left-0 right-0 bottom-0 h-0.5 bg-green-500/10"
                            aria-hidden
                          >
                            <div
                              className="h-full bg-gradient-to-r from-green-500/40 to-green-400 transition-[width] duration-1000 ease-linear"
                              style={{ width: `${Math.round(progress * 100)}%` }}
                              data-testid={`bot-tick-progress-${bot.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
